import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserRole, User } from "@/lib/types";
import { useAuth } from "@/hooks/useAuth";

export function useUserRoles() {
  const queryClient = useQueryClient();
  const { user, getAccessToken } = useAuth();

  const fetchUserRoles = async (userId?: string): Promise<UserRole[]> => {
    const targetId = userId || user?.id;
    
    if (!targetId) {
      console.warn("fetchUserRoles called without a userId");
      return [];
    }

    try {
      const { data, error } = await supabase
        .rpc('get_user_roles', { user_id: targetId });

      if (error) {
        console.error("Error fetching user roles:", error);
        return [];
      }

      console.log("Fetched user roles:", data);
      
      // Ensure all roles are properly normalized to uppercase
      return (data as UserRole[]).map(role => 
        typeof role === 'string' ? role.toUpperCase() as UserRole : role
      );
    } catch (err) {
      console.error("Exception in fetchUserRoles:", err);
      return [];
    }
  };

  const fetchAllUsers = async (): Promise<User[]> => {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      toast.error("Authentication required");
      return [];
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-get-users`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error fetching users: ${response.statusText}`);
      }

      const users = await response.json();
      
      // Make sure each user has a roles array
      return users.map((user: any) => ({
        ...user,
        roles: Array.isArray(user.roles) ? user.roles : []
      }));
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error instanceof Error) {
        toast.error(`Error fetching users: ${error.message}`);
      } else {
        toast.error("Error fetching users: Unknown error");
      }
      return [];
    }
  };

  const addRole = async ({ userId, role }: { userId: string; role: UserRole }): Promise<void> => {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      toast.error("Authentication required");
      throw new Error("Authentication required");
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-add-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ userId, role })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error adding role: ${response.statusText}`);
      }

      toast.success(`Role ${role} added to user`);
    } catch (error) {
      console.error("Error adding role:", error);
      if (error instanceof Error) {
        toast.error(`Error adding role: ${error.message}`);
      } else {
        toast.error("Error adding role: Unknown error");
      }
      throw error;
    }
  };

  const removeRole = async ({ userId, role }: { userId: string; role: UserRole }): Promise<void> => {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      toast.error("Authentication required");
      throw new Error("Authentication required");
    }

    if (role === "USER") {
      toast.error("Cannot remove the USER role");
      return;
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-remove-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ userId, role })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error removing role: ${response.statusText}`);
      }

      toast.success(`Role ${role} removed from user`);
    } catch (error) {
      console.error("Error removing role:", error);
      if (error instanceof Error) {
        toast.error(`Error removing role: ${error.message}`);
      } else {
        toast.error("Error removing role: Unknown error");
      }
      throw error;
    }
  };

  const hasRole = async (roleToCheck: UserRole): Promise<boolean> => {
    if (!user) {
      console.log("hasRole check failed: No user logged in");
      return false;
    }
    
    const normalizedRoleToCheck = roleToCheck.toUpperCase() as UserRole;
    console.log(`Checking role: ${normalizedRoleToCheck} for user: ${user.id}`);
    
    try {
      const { data, error } = await supabase
        .rpc('has_role', { 
          user_id: user.id,
          role: normalizedRoleToCheck
        });

      if (error) {
        console.error("Error checking role:", error, "for role:", normalizedRoleToCheck);
        // Manual check as fallback
        const userRoles = await fetchUserRoles();
        console.log("Manual role check:", userRoles, "Looking for:", normalizedRoleToCheck);
        
        // Apply case-insensitive comparison for extra safety
        const hasMatchingRole = userRoles.some(role => {
          const normalizedRole = (typeof role === 'string' ? role.toUpperCase() : role);
          return normalizedRole === normalizedRoleToCheck;
        });
        
        console.log(`Manual role check result: ${hasMatchingRole}`);
        return hasMatchingRole;
      }

      console.log(`has_role check for ${normalizedRoleToCheck}:`, data);
      return !!data;
    } catch (err) {
      console.error("Exception in hasRole check:", err);
      // Fallback to manual check if RPC fails
      try {
        const userRoles = await fetchUserRoles();
        console.log("Fallback role check:", userRoles, "Looking for:", normalizedRoleToCheck);
        
        // Apply case-insensitive comparison
        const hasMatchingRole = userRoles.some(role => {
          const normalizedRole = (typeof role === 'string' ? role.toUpperCase() : role);
          return normalizedRole === normalizedRoleToCheck;
        });
        
        console.log(`Fallback role check result: ${hasMatchingRole}`);
        return hasMatchingRole;
      } catch (fallbackErr) {
        console.error("Even fallback role check failed:", fallbackErr);
        return false;
      }
    }
  };

  const createTestNotification = async (
    { userId, title, message, type }: 
    { userId?: string; title?: string; message?: string; type?: "reminder" | "system" | "info" }
  ): Promise<void> => {
    const targetId = userId || user?.id;
    
    if (!targetId) {
      toast.error("No user specified");
      return;
    }

    const { data, error } = await supabase
      .rpc('generate_test_notification', {
        target_user_id: targetId,
        notification_title: title || "Test Notification",
        notification_message: message || "This is a test notification",
        notification_type: type || "system"
      });

    if (error) {
      toast.error(`Error creating test notification: ${error.message}`);
      throw error;
    }

    toast.success("Test notification created successfully");
  };

  const deleteUser = async (userId: string): Promise<void> => {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      toast.error("Authentication required");
      throw new Error("Authentication required");
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error deleting user: ${response.statusText}`);
      }

      const data = await response.json();
      toast.success(data.message || "User deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      if (error instanceof Error) {
        toast.error(`Error deleting user: ${error.message}`);
      } else {
        toast.error("Error deleting user: Unknown error");
      }
      throw error;
    }
  };

  const useUserRolesQuery = (userId?: string) => 
    useQuery({
      queryKey: ["userRoles", userId || user?.id],
      queryFn: () => fetchUserRoles(userId),
      enabled: !!user || !!userId,
    });

  const useAllUsersQuery = () => 
    useQuery({
      queryKey: ["allUsers"],
      queryFn: fetchAllUsers,
    });

  const useHasRoleQuery = (role: UserRole) =>
    useQuery({
      queryKey: ["hasRole", user?.id, role.toUpperCase()],
      queryFn: () => hasRole(role),
      enabled: !!user,
      staleTime: 10 * 60 * 1000, // Cache for 10 minutes
      retry: 3, // Increase retries for role checks
      retryDelay: attempt => Math.min(attempt > 1 ? 2000 : 1000, 30 * 1000),
    });

  const useAddRoleMutation = () => 
    useMutation({
      mutationFn: addRole,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["userRoles"] });
        queryClient.invalidateQueries({ queryKey: ["allUsers"] });
        queryClient.invalidateQueries({ queryKey: ["hasRole"] });
      }
    });

  const useRemoveRoleMutation = () => 
    useMutation({
      mutationFn: removeRole,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["userRoles"] });
        queryClient.invalidateQueries({ queryKey: ["allUsers"] });
        queryClient.invalidateQueries({ queryKey: ["hasRole"] });
      }
    });

  const useCreateTestNotificationMutation = () => 
    useMutation({
      mutationFn: createTestNotification,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    });

  const useDeleteUserMutation = () => 
    useMutation({
      mutationFn: deleteUser,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["allUsers"] });
      }
    });

  return {
    useUserRolesQuery,
    useAllUsersQuery,
    useHasRoleQuery,
    useAddRoleMutation,
    useRemoveRoleMutation,
    useCreateTestNotificationMutation,
    useDeleteUserMutation,
  };
}
